import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { CountryDetectorService, StepService } from '@lx/quote-tool/shared/services';
import { FormFieldComponent } from '@lx/shared/ui/form-field';
import { PaginatorModule } from 'primeng/paginator';
import { of, Subject, switchMap, takeUntil } from 'rxjs';
import { ResidenceCountry, ResidenceCountryService } from '@lx/shared/data-access/destination';
import { QuoteToolStore } from '@lx/quote-tool/shared/store';
import { NgIf } from '@angular/common';
import { PhonePrefixSanitizerDirective } from '@lx/quote-tool/shared/ui/directives';
import { InputMaskModule } from 'primeng/inputmask';
import { DEFAULT_ERROR_MESSAGE, PHONE_ERROR_MESSAGE } from '@lx/shared/errors';
import { MINIMUM_PHONE_LENGTH } from '@lx/quote-tool/shared/validators';

@Component({
  standalone: true,
  selector: 'lx-phone-number-field',
  imports: [FormFieldComponent, PaginatorModule, NgIf, PhonePrefixSanitizerDirective, InputMaskModule],
  templateUrl: './phone-number-field.component.html',
  styleUrls: ['./phone-number-field.component.scss'],
})
export class PhoneNumberFieldComponent implements OnInit, OnChanges, OnDestroy {
  @Input() label = '';
  @Input() isValid: null | boolean = false;
  @Input() required = false;
  @Input() phoneCode = '';
  @Input() phoneNumber = '';
  @Input() validationRequests = 0;
  @Output() valueChange = new EventEmitter<string>();

  protected countries: ResidenceCountry[] = [];
  protected prefixes: string[] = [];
  protected errorMessage = '';
  protected validationEnabled = false;

  private readonly destroy$ = new Subject<void>();
  private readonly stepService = inject(StepService);
  private readonly quoteToolStore = inject(QuoteToolStore);
  private readonly residenceCountryService = inject(ResidenceCountryService);
  private readonly countryDetectorService = inject(CountryDetectorService);

  ngOnInit(): void {
    // First, try to take the countries from store
    // but if they weren't loaded because we have not passed from residence country selector,
    // get them from residenceCountryService service
    this.quoteToolStore.countriesOfResidence$
      .pipe(
        switchMap((countries) => (!countries?.length ? this.residenceCountryService.getAll() : of(countries))),
        takeUntil(this.destroy$),
      )
      .subscribe((countries) => {
        this.countries = countries;
        this.prefixes = countries?.map((c) => c.phoneCode).filter(Boolean) ?? [];
        const residenceDataPhoneCode = this.stepService.getResidenceData()?.countryPhoneCode;
        const countryDetectorPhoneCode = countries?.find((country) => country?.code === this.countryDetectorService.detectCountryCode())?.phoneCode;
        if (!this.phoneCode) {
          this.phoneCode = residenceDataPhoneCode || countryDetectorPhoneCode;
        }
      });
  }

  ngOnChanges({ validationRequests }: SimpleChanges): void {
    if (validationRequests?.currentValue > 0) {
      this.enableValidation();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onValueChange(): void {
    this.emitValue();
    this.updateErrorMessage();
  }

  onInputBlur(): void {
    this.enableValidation(true);
  }

  private enableValidation(emit = false): void {
    if (this.validationEnabled) {
      return;
    }

    this.validationEnabled = true;
    this.updateErrorMessage();

    if (emit) {
      this.emitValue();
    }
  }

  private updateErrorMessage(): void {
    if (!this.validationEnabled) {
      this.errorMessage = '';
      return;
    }

    if (this.phoneNumber === null || this.phoneNumber === '') {
      this.errorMessage = DEFAULT_ERROR_MESSAGE;
      return;
    }

    if (this.phoneNumber?.length < MINIMUM_PHONE_LENGTH) {
      this.errorMessage = PHONE_ERROR_MESSAGE;
      return;
    }

    this.errorMessage = '';
  }

  private emitValue(): void {
    this.valueChange.emit(this.phoneCode + ' ' + (this.phoneNumber ?? ''));
  }
}
